import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { safeAdminNext } from "@/lib/return-to";
import { BrandPuzzle } from "@/components/cabinet/BrandPuzzle";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [{ title: "Вход — Yolutap" }, { name: "robots", content: "noindex" }],
    links: [
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Route a signed-in session by role: platform admins → /admin, everyone
  // else → /cabinet. Admin lives in user_roles (has_role); a user can read
  // their OWN roles (RLS "users can view own roles"). Server functions are
  // already role-gated — this is the client-side UX gate.
  const routeByRole = async () => {
    const { data: sess } = await supabase.auth.getSession();
    const uid = sess.session?.user.id;
    if (!uid) return;
    const { data: adminRole } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", uid)
      .eq("role", "admin")
      .maybeSingle();
    // Админ пришёл по ссылке из письма (/admin/review/<id>) — туда же и вернуть.
    const next = adminRole ? safeAdminNext(new URLSearchParams(window.location.search).get("next")) : null;
    if (next) window.location.replace(next);
    else navigate({ to: adminRole ? "/admin" : "/cabinet" });
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) routeByRole();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/auth` },
        });
        if (error) throw error;
      }
      // Only route when a session actually exists (signUp with email
      // confirmation returns no session yet) — role decides the destination.
      await routeByRole();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ошибка");
    } finally {
      setLoading(false);
    }
  };

  const fieldCls =
    "w-full rounded-xl border border-yt-line bg-white px-4 py-3 text-base text-yt-ink outline-none transition focus:border-yt-teal focus:ring-2 focus:ring-yt-teal/20";

  return (
    <div className="flex min-h-screen items-center justify-center bg-yt-cream p-4 font-yt-sans text-yt-ink">
      <div className="w-full max-w-md rounded-[17px] border border-yt-line bg-white p-6 sm:p-8">
        <div className="mb-5 flex justify-center">
          <BrandPuzzle />
        </div>
        <h1 className="text-center font-yt-serif text-2xl font-semibold">
          {mode === "login" ? "Вход" : "Регистрация"}
        </h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="email" className="block text-sm font-semibold text-yt-ink/70">
              Email
            </label>
            <input
              id="email"
              type="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
              className={fieldCls}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="password" className="block text-sm font-semibold text-yt-ink/70">
              Пароль
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              className={fieldCls}
            />
          </div>
          {error && <p className="text-sm text-red-700">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-yt-amber px-6 py-3 text-base font-bold text-yt-ink transition hover:brightness-[1.03] disabled:opacity-50"
          >
            {loading ? "..." : mode === "login" ? "Войти" : "Зарегистрироваться"}
          </button>
          <button
            type="button"
            onClick={() => setMode(mode === "login" ? "signup" : "login")}
            className="w-full text-center text-sm font-medium text-yt-teal underline"
          >
            {mode === "login" ? "Нет аккаунта? Зарегистрироваться" : "Уже есть аккаунт? Войти"}
          </button>
          <div className="text-center">
            <Link to="/" className="text-xs font-medium text-yt-teal underline">
              ← На главную
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
